/**
 * W1「窮人版滿屏」level b — 手機直持轉橫提示 overlay。
 *
 * 背景:遊戲畫布 800×600(4:3)+ Scale.FIT,手機直持(412×915)時畫布只佔
 * 螢幕 1/3(上下黑帶,證據 _game-feel-shots/03-hub.png)。橫持(915×412)
 * FIT 後畫布幾乎滿屏。完整 RESIZE 是 W4 主線;本模組只做低風險前菜:
 * 偵測 portrait + 觸控裝置時,蓋一層學術風深色提示「把裝置轉橫」。
 *
 * 設計約束:
 *  - 顯示條件純 CSS media query `(orientation: portrait) and (pointer: coarse)`
 *    → 桌機(滑鼠)永遠看不到;轉橫即自動消失,零 JS resize listener。
 *  - 非 alert:深色漸層(沿用 hub 夜空 #10131e/#141b2a 調)+ 金色 accent,
 *    含旋轉手機動畫 icon。
 *  - 可關閉:「繼續直向遊玩」→ sessionStorage 記住本次瀏覽 session,
 *    不強制(小孩平板可能鎖直向)。
 *  - 掛在 #game-container 內 → 跟著 main.ts setMode 的 display 切換,
 *    學習端永遠看不到。
 *  - 鐵則:純 DOM 提示,0 題目、0 戰鬥數值。
 */

const STYLE_ID = 'ef-rotate-hint-style';
const HINT_ID = 'ef-rotate-hint';
/** sessionStorage key — 本次 session 內按過「繼續直向遊玩」就不再顯示。 */
export const ROTATE_HINT_DISMISS_KEY = 'ef.rotateHint.dismissed';

const CSS = `
.ef-rotate-hint { display: none; }
@media (orientation: portrait) and (pointer: coarse) {
  .ef-rotate-hint:not(.ef-rotate-hint--dismissed) { display: flex; }
}
.ef-rotate-hint {
  position: absolute; inset: 0; z-index: 60;
  flex-direction: column; align-items: center; justify-content: center;
  gap: 14px; padding: 24px; box-sizing: border-box; text-align: center;
  background: linear-gradient(168deg, #0c101c 0%, #18203a 52%, #0c101c 100%);
  color: #e9e6da;
}
.ef-rotate-hint__phone {
  width: 44px; height: 76px; border-radius: 10px;
  border: 3px solid #ffd54f; position: relative;
  animation: ef-rotate-hint-turn 2.4s ease-in-out infinite;
}
.ef-rotate-hint__phone::after {
  content: ''; position: absolute; left: 50%; bottom: 6px;
  width: 14px; height: 3px; border-radius: 2px;
  transform: translateX(-50%); background: #ffd54f;
}
@keyframes ef-rotate-hint-turn {
  0%, 25%  { transform: rotate(0deg); }
  55%, 80% { transform: rotate(90deg); }
  100%     { transform: rotate(90deg); }
}
.ef-rotate-hint__title {
  font-size: 20px; font-weight: 700; color: #ffd54f;
  letter-spacing: 1px; margin: 0;
}
.ef-rotate-hint__sub {
  font-size: 14px; opacity: .82; margin: 0; line-height: 1.6;
}
.ef-rotate-hint__dismiss {
  margin-top: 10px; padding: 10px 22px; font-size: 14px;
  color: #cfd6e6; background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.22); border-radius: 999px;
  cursor: pointer; touch-action: manipulation;
}
.ef-rotate-hint__dismiss:active { background: rgba(255,255,255,.14); }
`;

/**
 * Mount the portrait rotate hint into the game container. Idempotent —
 * repeated calls (e.g. main.ts re-entering game modes) are no-ops while the
 * overlay is already in the DOM. Returns an unmount function.
 */
export function mountRotateHint(container?: HTMLElement | null): () => void {
  const host = container ?? document.getElementById('game-container');
  if (!host) return () => { /* no game container (test harness) */ };
  const existing = document.getElementById(HINT_ID);
  if (existing) return () => existing.remove();

  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const root = document.createElement('div');
  root.id = HINT_ID;
  root.className = 'ef-rotate-hint';
  let dismissed = false;
  try { dismissed = sessionStorage.getItem(ROTATE_HINT_DISMISS_KEY) === '1'; } catch { /* storage blocked */ }
  if (dismissed) root.classList.add('ef-rotate-hint--dismissed');
  root.innerHTML = `
    <div class="ef-rotate-hint__phone" aria-hidden="true"></div>
    <p class="ef-rotate-hint__title">把裝置轉橫,戰場更大</p>
    <p class="ef-rotate-hint__sub">橫向時畫面幾乎滿屏,<br>操作按鈕也不會擋住戰車。</p>
    <button type="button" class="ef-rotate-hint__dismiss">繼續直向遊玩</button>
  `;
  root.querySelector('.ef-rotate-hint__dismiss')?.addEventListener('click', () => {
    root.classList.add('ef-rotate-hint--dismissed');
    try { sessionStorage.setItem(ROTATE_HINT_DISMISS_KEY, '1'); } catch { /* storage blocked */ }
  });
  host.appendChild(root);
  return () => root.remove();
}
